import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Button, Grid } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumClaseFactura, EnumIdioma, EnumTipoFactura } from '../../../api'
import { EnumSubTipoConfiguracion } from '../../../api/oData/enums/enumSubTipoConfiguracion'
import { EnumTipoConfiguracion } from '../../../api/oData/enums/enumTipoConfiguracion'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumToSelectOptionArray } from '../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { NonAuthRoutes } from '../../../model'
import { getActiveCompany, getCustomerDomain } from '../../../redux'
import { TemplateSettingsAdd } from '../templateSettings.model'
import {
  createTemplateSettings,
  fetchClientListItems,
  fetchContractTypeListItems
} from '../templateSettings.service'

export interface TemplateSettingsAddPageProps {
  location?: {
    state: {
      templateType: EnumTipoConfiguracion
      productsList: SelectOption[]
    }
  }
}

export const TemplateSettingsAddPage: React.FC<TemplateSettingsAddPageProps> = props => {
  const [contractTypeList, setContractTypeList] = useState<SelectOption[]>([])
  const [clientList, setClientList] = useState<SelectOption[]>([])
  const history = useHistory()
  const { info, error } = useNotify()
  const [t] = useTranslation(['globalerisLibrary', 'templateSettings', 'enums'])

  const templateType: EnumTipoConfiguracion = props.location?.state?.templateType
    ? props.location?.state?.templateType
    : EnumTipoConfiguracion.Plantilla

  const productsList: SelectOption[] = props.location?.state?.productsList
    ? props.location?.state?.productsList
    : []

  const initialFormData: TemplateSettingsAdd = {
    templateType: templateType,
    currentVersion: 1,
    company: getActiveCompany()?.id
  }

  const [dataForm, setDataForm] = useState<TemplateSettingsAdd | undefined>(initialFormData)

  //#region Form and Datatable Definitions
  const formSections: Section[] = [
    {
      inputs: [
        {
          name: 'id',
          display: false
        },
        {
          name: 'templateType',
          label: t('templateSettings:dataTable.templateType'),
          readOnly: true,
          type: DataType.SELECT,
          required: true,
          selectOptions: enumToSelectOptionArray(
            EnumTipoConfiguracion,
            'enums:EnumTipoConfiguracion'
          )
        },
        {
          name: 'templateSubType',
          label: t('templateSettings:dataTable.templateSubType'),
          readOnly: false,
          type: DataType.SELECT,
          display: templateType === EnumTipoConfiguracion.Widget,
          required: templateType === EnumTipoConfiguracion.Widget,
          selectOptions: enumToSelectOptionArray(
            EnumSubTipoConfiguracion,
            'enums:EnumSubTipoConfiguracion'
          )
        },
        {
          name: 'description',
          label: t('templateSettings:dataTable.description'),
          readOnly: false,
          required: true,
          type: DataType.TEXT
        },
        {
          name: 'products',
          label: t('templateSettings:dataTable.products'),
          type: DataType.MULTISELECT,
          selectOptions: productsList
        },
        {
          name: 'contractType',
          label: t('templateSettings:dataTable.contractType'),
          type: DataType.MULTISELECT,
          selectOptions: contractTypeList
        },
        {
          name: 'client',
          label: t('templateSettings:dataTable.client'),
          type: DataType.MULTISELECT,
          selectOptions: clientList
        },
        {
          name: 'language',
          label: t('templateSettings:dataTable.language'),
          readOnly: false,
          type: DataType.SELECT,
          clearable: true,
          selectOptions: enumToSelectOptionArray(EnumIdioma, 'enums:EnumIdioma')
        },
        {
          name: 'invoiceType',
          label: t('templateSettings:dataTable.invoiceType'),
          readOnly: false,
          type: DataType.SELECT,
          clearable: true,
          selectOptions: enumToSelectOptionArray(EnumTipoFactura, 'enums:EnumTipoFactura')
        },
        {
          name: 'invoiceClass',
          label: t('templateSettings:dataTable.invoiceClass'),
          readOnly: false,
          type: DataType.SELECT,
          clearable: true,
          selectOptions: enumToSelectOptionArray(EnumClaseFactura, 'enums:EnumClaseFactura')
        },
        {
          name: 'currentVersion',
          label: t('templateSettings:dataTable.currentVersion'),
          type: DataType.TEXT,
          readOnly: true
        }
      ]
    }
  ]

  const formOptions: FormOptions = {
    elevation: 1,
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('templateSettings:add.template')
    }
  }

  //#endregion

  //#region Handlers
  const onSaveHandler = async () => {
    if (dataForm && !missingFieldsFormWithRequiredText(formSections, dataForm)) {
      await createTemplateSettings(dataForm, productsList, contractTypeList, clientList).then(
        response => {
          if (response.success) {
            info(t('templateSettings:forms.add.notifications.save.success'))
            history.goBack()
          } else {
            error(t('templateSettings:forms.add.notifications.save.error'))
          }
        }
      )
    } else {
      error(t('templateSettings:forms.add.notifications.incompleteForm'))
    }
  }
  //#endregion

  //#region useEffect

  useEffect(() => {
    const getContractTypeSelectOptionList = async () => {
      await fetchContractTypeListItems().then(response => {
        if (response.success && response.data) {
          setContractTypeList(response.data)
        } else {
          error(t('templateSettings:notifications.contractTypeListError'))
        }
      })
    }

    const getClientSelectOptionList = async () => {
      await fetchClientListItems().then(response => {
        if (response.success && response.data) {
          setClientList(response.data)
        } else {
          error(t('templateSettings:notifications.clientListError'))
        }
      })
    }

    getContractTypeSelectOptionList()
    getClientSelectOptionList()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //#endregion

  return (
    <Page>
      <PageHeader title={t('templateSettings:add.title')}>
        <Grid container direction="row" justify="flex-end" spacing={2}>
          <Grid item>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => {
                history.push(NonAuthRoutes.TemplateSettings)
              }}
            >
              {t('templateSettings:button.back')}
            </Button>
          </Grid>
          <Grid item>
            <Button size="small" variant="contained" color="primary" onClick={onSaveHandler}>
              {t('templateSettings:button.save')}
            </Button>
          </Grid>
        </Grid>
      </PageHeader>
      <Form sections={formSections} data={dataForm} options={formOptions} />
    </Page>
  )
}
